export type WebsiteScanStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
export type WebsiteAnalysisStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLIANT' | 'NON_COMPLIANT' | 'FAILED';
export type VltStatus = 'ONLINE' | 'OFFLINE' | 'MAINTENANCE';
export type ScanStatus = 'QUEUED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
export type ScanStage = 'CAPTURE' | 'ANALYSIS' | 'DEAL_VERIFICATION' | 'REPORT';
export type ScanTriggerType = 'SCHEDULED' | 'MANUAL' | 'IMPORTED';

export interface Regulator {
  regulatorId: number;
  code: string;
  name: string;
  externalCode: string | null;
}

export interface RegulatorPayload {
  code: string;
  name: string;
  externalCode: string | null;
}

/** Rule of the external Compliance Scan service imported for a regulator. */
export interface RegulatorCheck {
  checkId: number;
  key: string;
  name: string;
  rule: string | null;
  regulation: string | null;
  position: number | null;
}

/** Client of a regulator (`/regulators/:regulatorId/clients`): each client belongs to one regulator. */
export interface RegulatorClient {
  clientId: number;
  regulatorId: number;
  code: string;
  name: string;
}

/** The regulator is taken from the path (a client cannot change regulator). */
export interface RegulatorClientPayload {
  code: string;
  name: string;
}

export interface RegulatorClientWebsite {
  websiteId: number;
  clientId: number;
  url: string;
  scanEnabled: boolean;
  scanStatus: WebsiteScanStatus;
  lastScanTime: string | null;
  analysisStatus: WebsiteAnalysisStatus;
  lastAnalysisTime: string | null;
  lastScanId: number | null;
}

export interface RegulatorWebsitePayload {
  clientId: number;
  url: string;
  scanEnabled: boolean;
}

export interface Scan {
  scanId: number;
  websiteId: number;
  externalScanId: string | null;
  status: ScanStatus;
  stage: ScanStage | null;
  url: string | null;
  finalUrl: string | null;
  reference: string | null;
  externalCreatedAt: string | null;
  completedAt: string | null;
  isCompliant: boolean | null;
  errorMessage: string | null;
  triggerType: ScanTriggerType;
  requestedByUserId: number | null;
  hasScreenshot: boolean;
  hasReport: boolean;
  createTime: string | null;
}

export interface ScanCheck {
  key: string;
  name: string;
  passed: boolean | null;
  message: string | null;
  position: number | null;
}

export interface ScanDetail extends Scan {
  checks: ScanCheck[];
  rawPayload: string | null;
}

export interface Vlt {
  vltId: number;
  regulatorId: number;
  code: string;
  serialNumber: string;
  manufacturer: string | null;
  model: string | null;
  venue: string;
  address: string | null;
  city: string;
  country: string | null;
  status: VltStatus;
  lastSeenTime: string | null;
  latitude: number | null;
  longitude: number | null;
}

export type VltPayload = Omit<Vlt, 'vltId' | 'regulatorId'>;

export const WEBSITE_SCAN_STATUS_LABELS: Record<WebsiteScanStatus, string> = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  FAILED: 'Failed'
};

export const WEBSITE_ANALYSIS_STATUS_LABELS: Record<WebsiteAnalysisStatus, string> = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In Progress',
  COMPLIANT: 'Compliant',
  NON_COMPLIANT: 'Non Compliant',
  FAILED: 'Failed'
};

export const VLT_STATUS_LABELS: Record<VltStatus, string> = {
  ONLINE: 'Online',
  OFFLINE: 'Offline',
  MAINTENANCE: 'Maintenance'
};

export const SCAN_STATUS_LABELS: Record<ScanStatus, string> = {
  QUEUED: 'Queued',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  FAILED: 'Failed'
};

export const SCAN_STAGE_LABELS: Record<ScanStage, string> = {
  CAPTURE: 'Capture',
  ANALYSIS: 'Analysis',
  DEAL_VERIFICATION: 'Deal Verification',
  REPORT: 'Report'
};

export const SCAN_TRIGGER_TYPE_LABELS: Record<ScanTriggerType, string> = {
  SCHEDULED: 'Scheduled',
  MANUAL: 'Manual',
  IMPORTED: 'Imported'
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'secondary',
  QUEUED: 'secondary',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  COMPLIANT: 'success',
  NON_COMPLIANT: 'danger',
  FAILED: 'danger',
  ONLINE: 'success',
  OFFLINE: 'danger',
  MAINTENANCE: 'warning',
  PASSED: 'success'
};

export const websiteScanStatusLabel = (status: string | null | undefined): string =>
  status ? (WEBSITE_SCAN_STATUS_LABELS[status as WebsiteScanStatus] ?? status) : '-';

export const websiteAnalysisStatusLabel = (status: string | null | undefined): string =>
  status ? (WEBSITE_ANALYSIS_STATUS_LABELS[status as WebsiteAnalysisStatus] ?? status) : '-';

export const vltStatusLabel = (status: string | null | undefined): string =>
  status ? (VLT_STATUS_LABELS[status as VltStatus] ?? status) : '-';

export const scanStatusLabel = (status: string | null | undefined): string =>
  status ? (SCAN_STATUS_LABELS[status as ScanStatus] ?? status) : '-';

export const scanStageLabel = (stage: string | null | undefined): string =>
  stage ? (SCAN_STAGE_LABELS[stage as ScanStage] ?? stage) : '-';

export const scanTriggerTypeLabel = (triggerType: string | null | undefined): string =>
  triggerType ? (SCAN_TRIGGER_TYPE_LABELS[triggerType as ScanTriggerType] ?? triggerType) : '-';

/** Status label of a scan, with the current stage when the scan is running (e.g. "In Progress · Analysis"). */
export const scanStatusWithStageLabel = (scan: Pick<Scan, 'status' | 'stage'> | null | undefined): string => {
  if (!scan?.status) {
    return '-';
  }
  const label = scanStatusLabel(scan.status);
  return scan.status === 'IN_PROGRESS' && scan.stage ? `${label} · ${scanStageLabel(scan.stage)}` : label;
};

export const regulatorStatusBadgeClass = (status: string | null | undefined): string =>
  `regulator-status-badge text-bg-${STATUS_COLORS[status ?? ''] ?? 'secondary'}`;

/** Compliance verdict of a scan: true → COMPLIANT, false → NON_COMPLIANT, null/undefined → null (not available). */
export const scanComplianceStatus = (isCompliant: boolean | null | undefined): WebsiteAnalysisStatus | null => {
  if (isCompliant === true) {
    return 'COMPLIANT';
  }
  if (isCompliant === false) {
    return 'NON_COMPLIANT';
  }
  return null;
};

export const scanComplianceLabel = (isCompliant: boolean | null | undefined): string | null => {
  const status = scanComplianceStatus(isCompliant);
  return status ? websiteAnalysisStatusLabel(status) : null;
};

export const scanComplianceBadgeClass = (isCompliant: boolean | null | undefined): string =>
  regulatorStatusBadgeClass(scanComplianceStatus(isCompliant));

export const scanCheckBadgeClass = (passed: boolean | null | undefined): string =>
  regulatorStatusBadgeClass(passed === true ? 'PASSED' : passed === false ? 'FAILED' : null);

export const scanCheckLabel = (passed: boolean | null | undefined): string =>
  passed === true ? 'Passed' : passed === false ? 'Failed' : '-';

export const vltHasCoordinates = (vlt: Vlt | null | undefined): boolean =>
  vlt?.latitude !== null && vlt?.latitude !== undefined &&
  vlt?.longitude !== null && vlt?.longitude !== undefined;
